import type { EmbedConfig } from './embedConfig'
import type { PublicEvent } from './publicEvent'

export const DATE_RANGES = ['all', 7, 14, 30] as const

export type DateRange = (typeof DATE_RANGES)[number]

export type SupporterChoices = {
  days: DateRange
  needsVolunteers: boolean
  onlineOnly: boolean
  eventTypeIds: number[]
  tags: Array<string | null>
}

export const NO_CHOICES: SupporterChoices = {
  days: 'all',
  needsVolunteers: false,
  onlineOnly: false,
  eventTypeIds: [],
  tags: [],
}

export type TagOption = { slug: string; name: string; spelling: string }

export type FilterControls = {
  date: boolean
  needsVolunteers: boolean
  onlineOnly: boolean
  eventTypes: PublicEvent['event_type'][]
  tagFilters: { position: number; label: string; options: TagOption[] }[]
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

function hasOpenSpots(event: PublicEvent) {
  const spots = event.spots.self_sign_up
  return spots.filled < spots.total
}

export function tagSlug(tag: string) {
  return tag
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
}

const hasMixedCase = (text: string) => /\p{Lu}/u.test(text) && /\p{Ll}/u.test(text)

function optionName(remainder: string) {
  if (hasMixedCase(remainder)) return remainder.split(/[\s_]+/).join(' ')
  return remainder
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const byName = (a: TagOption, b: TagOption) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })

function preferredSpelling(spellings: Set<string>, leadingPrefix: RegExp) {
  const candidates = Array.from(spellings, (spelling) => ({
    spelling,
    remainder: spelling.replace(leadingPrefix, ''),
  })).sort((a, b) => a.spelling.localeCompare(b.spelling))
  return candidates.find((candidate) => hasMixedCase(candidate.remainder)) ?? candidates[0]
}

export function tagOptions({ events, prefix }: { events: PublicEvent[]; prefix: string | null }): TagOption[] {
  const prefixSlug = tagSlug(prefix ?? '')
  const matches = (slug: string) => (prefixSlug ? slug.startsWith(`${prefixSlug}_`) : slug !== '')

  const spellings = new Map<string, Set<string>>()
  events.forEach((event) =>
    event.tags.forEach((tag) => {
      const slug = tagSlug(tag)
      if (matches(slug)) spellings.set(slug, (spellings.get(slug) ?? new Set()).add(tag))
    })
  )
  const leadingPrefix = prefixSlug
    ? new RegExp(`^[^a-z0-9]*${prefixSlug.split('_').join('[^a-z0-9]+')}[^a-z0-9]+`, 'i')
    : /^[^a-z0-9]*/i

  return Array.from(spellings, ([slug, spellingsOfSlug]) => {
    const { spelling, remainder } = preferredSpelling(spellingsOfSlug, leadingPrefix)
    return { slug, spelling, name: optionName(remainder) }
  }).sort(byName)
}

// Which controls the embed offers, judged on every event in scope rather than
// the ones left after choosing, so a control doesn't vanish once it's used.
export function filterControls({ events, config }: { events: PublicEvent[]; config: EmbedConfig }): FilterControls {
  const eventTypes = Array.from(new Map(events.map((event) => [event.event_type.id, event.event_type])).values())
  const tagFilters = config.tagFilters.flatMap(({ name, prefix, hiddenTags }, position) => {
    const options = tagOptions({ events, prefix }).filter(({ slug }) => !hiddenTags.includes(slug))
    return name && options.length > 0 ? [{ position, label: name, options }] : []
  })
  return {
    date: config.dateFilter,
    needsVolunteers:
      config.shifts && config.needsVolunteers && events.some((event) => event.spots.self_sign_up.total > 0),
    onlineOnly: config.onlineFilter && events.some((event) => event.online) && events.some((event) => !event.online),
    eventTypes: config.typeFilter && eventTypes.length > 1 ? eventTypes : [],
    tagFilters,
  }
}

// A choice counts only while its control is shown, so nothing stays hidden
// with no way to undo it.
export function activeChoices(choices: SupporterChoices, controls: FilterControls): SupporterChoices {
  const offeredTypes = new Set(controls.eventTypes.map(({ id }) => id))
  return {
    days: controls.date ? choices.days : 'all',
    needsVolunteers: controls.needsVolunteers && choices.needsVolunteers,
    onlineOnly: controls.onlineOnly && choices.onlineOnly,
    eventTypeIds: choices.eventTypeIds.filter((id) => offeredTypes.has(id)),
    tags: choices.tags.map((tag, position) =>
      controls.tagFilters.find((filter) => filter.position === position)?.options.some(({ slug }) => slug === tag)
        ? tag
        : null
    ),
  }
}

export function hasChoices({ days, needsVolunteers, onlineOnly, eventTypeIds, tags }: SupporterChoices) {
  return days !== 'all' || needsVolunteers || onlineOnly || eventTypeIds.length > 0 || tags.some(Boolean)
}

export function applyChoices({
  events,
  choices: { days, needsVolunteers, onlineOnly, eventTypeIds, tags },
  now,
}: {
  events: PublicEvent[]
  choices: SupporterChoices
  now: Date
}) {
  const until = days === 'all' ? null : now.getTime() + days * MS_PER_DAY

  return events.filter(
    (event) =>
      (until === null || (event.starts_at !== null && new Date(event.starts_at).getTime() <= until)) &&
      (!needsVolunteers || hasOpenSpots(event)) &&
      (!onlineOnly || event.online) &&
      (eventTypeIds.length === 0 || eventTypeIds.includes(event.event_type.id)) &&
      tags.every((tag) => tag === null || event.tags.some((raw) => tagSlug(raw) === tag))
  )
}
